import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCopy, FiCheck, FiClock, FiDollarSign, FiSend } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import { getPlanById } from '@/config/planConfig';
import { usePaymentSettings } from '@/hooks/usePaymentSettings';

interface CryptoPaymentModalProps {
  planId: string;
  billingCycle?: 'monthly' | 'yearly';
  /**
   * Optional promo code from a Founding Member claim (via
   * ?founding=<code> on /account/billing). If set, we apply the
   * FOUNDING_PCT discount to the crypto invoice amount so the user
   * pays the same discounted price they'd see in Stripe Checkout.
   *
   * NOTE: Stripe enforces the seat cap AND the coupon existence for
   * its side. Crypto is admin-confirmed so the same seat_taken
   * decrement already ran when the user clicked "Claim" on /founding
   * — this prop is just the discount signal, not a claim attempt.
   */
  foundingPromo?: string;
  onClose: () => void;
  onSuccess: () => void;
}

// Keep in sync with the FOUNDING40 Stripe coupon and the AccountBilling
// preview number. If you launch another Founding batch at a different
// pct, thread the value through from get_active_founding_batch instead
// of hardcoding.
const FOUNDING_PCT = 40;

const CryptoPaymentModal: React.FC<CryptoPaymentModalProps> = ({ planId, billingCycle = 'monthly', foundingPromo, onClose, onSuccess }) => {
  const [step, setStep] = useState<'info' | 'submit'>('info');
  const [txHash, setTxHash] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const plan = getPlanById(planId);
  const paymentConfig = usePaymentSettings();

  const isAnnual = billingCycle === 'yearly';
  const listPrice = plan?.priceMonthly
    ? (isAnnual ? Math.round(plan.priceMonthly * 12 * (plan.yearlyDiscount || 0.80)) : plan.priceMonthly)
    : 0;
  const hasFounding = !!foundingPromo && listPrice > 0;
  const displayPrice = hasFounding
    ? Math.round(listPrice * (100 - FOUNDING_PCT)) / 100
    : listPrice;
  const billingLabel = isAnnual ? 'year' : 'month';

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSubmitTxHash = async () => {
    if (!txHash.trim()) {
      setError('Please enter the transaction hash');
      return;
    }
    if (txHash.trim().length < 10) {
      setError('Transaction hash seems too short');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: insertError } = await supabase
        .from('crypto_invoices')
        .insert({
          user_id: user.id,
          plan_id: planId,
          amount_usd: displayPrice,
          crypto_currency: paymentConfig.cryptoCurrency,
          payment_address: paymentConfig.cryptoAddress,
          tx_hash: txHash.trim(),
          status: 'pending',
          billing_type: isAnnual ? 'annual' : 'monthly',
          notes: hasFounding
            ? `${paymentConfig.cryptoCurrency} ${paymentConfig.cryptoNetwork} payment (${billingLabel}) · FOUNDING ${FOUNDING_PCT}% off applied (was $${listPrice}, code ${foundingPromo})`
            : `${paymentConfig.cryptoCurrency} ${paymentConfig.cryptoNetwork} payment (${billingLabel})`,
        });

      if (insertError) throw insertError;

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting tx hash:', err);
      setError(err.message || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-bold text-foreground">Pay with {paymentConfig.cryptoCurrency}</h2>
              <p className="text-sm text-muted-foreground">{plan?.name} — ${displayPrice}/{billingLabel}</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
              <FiX className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          <div className="p-6">
            {success ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <FiCheck className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Payment Submitted!</h3>
                <p className="text-sm text-muted-foreground">
                  Your payment will be verified and your subscription activated within 24 hours.
                </p>
              </div>
            ) : step === 'info' ? (
              <>
                {/* Payment Details */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-600/20 to-cyan-600/20 flex items-center justify-center">
                    <FiDollarSign className="w-8 h-8 text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">Send Payment</h3>
                  <p className="text-sm text-muted-foreground">
                    Send exactly <strong>${displayPrice} USD</strong> in {paymentConfig.cryptoCurrency} ({paymentConfig.cryptoNetwork})
                  </p>
                  {/* Founding Member banner — parity with the Stripe path,
                      so crypto payers see they got the same 40%-off-for-life
                      deal instead of quietly paying the list price. */}
                  {hasFounding && (
                    <div className="mt-4 mx-auto max-w-xs rounded-xl border border-cyan-400/40 bg-cyan-500/10 px-4 py-3 text-left">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300 mb-1">
                        ▸ Founding member · {FOUNDING_PCT}% off
                      </div>
                      <div className="flex items-baseline gap-2 text-sm">
                        <span className="text-gray-400 line-through">${listPrice}</span>
                        <span className="text-white font-bold">${displayPrice}</span>
                        <span className="text-gray-500">/{billingLabel}</span>
                      </div>
                      <div className="text-[11px] text-gray-400 mt-1">
                        Locked in for life. Never expires.
                      </div>
                    </div>
                  )}
                </div>

                {/* Address */}
                <div className="bg-background/50 rounded-xl p-4 mb-4">
                  <label className="block text-xs text-muted-foreground mb-1">
                    {paymentConfig.cryptoCurrency} Address ({paymentConfig.cryptoNetwork})
                  </label>
                  <div className="flex items-start gap-2">
                    <code className="flex-1 text-sm text-foreground break-all font-mono">
                      {paymentConfig.cryptoAddress}
                    </code>
                    <button
                      onClick={() => copyToClipboard(paymentConfig.cryptoAddress)}
                      className="p-2 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
                    >
                      {copied ? (
                        <FiCheck className="w-5 h-5 text-green-400" />
                      ) : (
                        <FiCopy className="w-5 h-5 text-muted-foreground" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Amount */}
                <div className="bg-background/50 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Amount Due</span>
                    <span className="text-xl font-bold text-foreground">${displayPrice} USD</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Send equivalent in {paymentConfig.cryptoCurrency} on {paymentConfig.cryptoNetwork} network
                  </p>
                </div>

                {/* Instructions */}
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 mb-6">
                  <p className="text-sm text-yellow-200">
                    <strong>Important:</strong> Make sure to use the <strong>{paymentConfig.cryptoNetwork}</strong> network. 
                    Sending on a wrong network may result in loss of funds.
                  </p>
                </div>

                <button
                  onClick={() => setStep('submit')}
                  className="w-full py-3 px-6 bg-gray-900/80 border border-purple-500/30 text-white font-medium rounded-xl hover:bg-gray-800/80 hover:border-purple-500/50 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <FiSend className="w-5 h-5" />
                  I've Sent the Payment
                </button>
              </>
            ) : (
              <>
                {/* TX Hash Submission */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-600/20 to-cyan-600/20 flex items-center justify-center">
                    <FiClock className="w-8 h-8 text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">Submit Transaction Hash</h3>
                  <p className="text-sm text-muted-foreground">
                    Paste your transaction hash below for verification
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-foreground mb-2">Transaction Hash (TX ID)</label>
                  <input
                    type="text"
                    value={txHash}
                    onChange={(e) => setTxHash(e.target.value)}
                    placeholder="e.g. 0x1234abcd..."
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-purple-500 font-mono text-sm"
                  />
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <button
                    onClick={handleSubmitTxHash}
                    disabled={submitting}
                    className="w-full py-3 px-6 bg-gray-900/80 border border-purple-500/30 text-white font-medium rounded-xl hover:bg-gray-800/80 hover:border-purple-500/50 transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <FiSend className="w-5 h-5" />
                        Submit for Verification
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setStep('info')}
                    className="w-full py-2 px-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    ← Back to payment details
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CryptoPaymentModal;
